import { useCallback, useEffect, useRef, useState } from "react";
import { useReactFlow } from "reactflow";
import { useDispatch } from "react-redux";
import store from "redux/store";
import { setContext } from "redux/actions/flowDesigner/editorContextActionCreator";

const defaultOptions = {
  maxHistorySize: 100,
  enableShortcuts: true
};

// https://redux.js.org/usage/implementing-undo-history
export const useUndoRedo = (
  saveFlow = Function(),
  {
    maxHistorySize = defaultOptions.maxHistorySize,
    enableShortcuts = defaultOptions.enableShortcuts
  } = defaultOptions
) => {
  // the past and future arrays store the states that we can jump to
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  // const reactFlowInstance = useReactFlow();
  const { setNodes, setEdges, getNodes, getEdges } = useReactFlow();
  const dispatch = useDispatch();

  const getContext = useCallback(() => JSON.parse(JSON.stringify(store.getState().designer.editorContext.context)), []);
  const restoreContext = useCallback((ctx) => dispatch(setContext(JSON.parse(JSON.stringify(ctx)))), [dispatch]);

  const isCanvasActiveRef = useRef(false);
  const lastSnapshotTimeRef = useRef(0);

  const getCurrentState = useCallback(() => ({
    nodes: getNodes(),
    edges: getEdges(),
    context: getContext?.()
  }), [getNodes, getEdges, getContext]);

  const takeSnapshot = useCallback(() => {
    const now = Date.now();
    if (now - lastSnapshotTimeRef.current < 200) {
      return;
    }
    lastSnapshotTimeRef.current = now;

    const currentState = getCurrentState();

    // push the current graph to the past state
    setPast((past) => [
      ...past.slice(past.length - maxHistorySize + 1, past.length),
      currentState
    ]);

    // whenever we take a new snapshot, the redo operations need to be cleared to avoid state mismatches
    setFuture([]);
  }, [maxHistorySize, getCurrentState]);

  const undo = useCallback(() => {
    // get the last state that we want to go back to
    const pastState = past[past.length - 1];

    if (pastState) {
      // first we remove the state from the history
      setPast((past) => past.slice(0, past.length - 1));

      const currentState = getCurrentState();

      // we store the current graph for the redo operation
      setFuture((future) => [...future, currentState]);
      // now we can set the graph to the past state
      setNodes(pastState.nodes);
      setEdges(pastState.edges);

      if (pastState.context !== undefined) {
        restoreContext?.(pastState.context);
      }

      saveFlow();
    }
  }, [past, restoreContext, getCurrentState]);

  const redo = useCallback(() => {
    const futureState = future[future.length - 1];

    if (futureState) {
      setFuture((future) => future.slice(0, future.length - 1));

      const currentState = getCurrentState();

      setPast((past) => [...past, currentState]);
      setNodes(futureState.nodes);
      setEdges(futureState.edges);

      if (futureState.context !== undefined) {
        restoreContext?.(futureState.context);
      }

      saveFlow();
    }
  }, [future, restoreContext, getCurrentState]);

  useEffect(() => {
    // this effect is used to attach the global event handlers
    if (!enableShortcuts) {
      return;
    }

    const keyDownHandler = (event) => {
      const key = event.key.toLowerCase();
      const isCtrlOrMeta = event.ctrlKey || event.metaKey;

      const isUndo = key === "z" && isCtrlOrMeta && !event.shiftKey;
      const isRedo = (key === "z" && isCtrlOrMeta && event.shiftKey) || (key === "y" && isCtrlOrMeta);

      if (!isUndo && !isRedo) return;

      if (!isCanvasActiveRef.current) return;

      event.preventDefault();

      if (isRedo) {
        redo();
      } else {
        undo();
      }
    };

    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [undo, redo, enableShortcuts]);

  return {
    undo,
    redo,
    takeSnapshot,
    canUndo: !past.length,
    canRedo: !future.length,
    isCanvasActiveRef
  };
};

export default useUndoRedo;
